import { useEffect } from 'react';

/**
 * Шуурхай/шууд хүргэлтийн захиалга үүсгэх урсгалын хагас дутуу мэдээллийг
 * (draft) хадгалах.
 *
 * Бүх төлөв нь zustand store-д (Add/store.js) байдаг тул хуудсаа refresh
 * хийхэд бөглөсөн мэдээлэл алга болдог байсан. sessionStorage-д хадгалснаар
 * refresh хийсэн ч захиалгууд, хаяг, формын утгууд сэргэнэ.
 *
 * Аль алхам дээр байгааг нь URL-ийн `?step=` параметр агуулна (Add.js), захиалга
 * тус бүрийн хаягийн табыг `?tab=` агуулна (useOrderTabs.js) — ингэснээр
 * refresh, browser-ийн back/forward хоёулаа ажиллана.
 *
 * ХАМРАХ ХҮРЭЭ: draft нь ЗӨВХӨН тухайн хуудас дээр байх хугацаанд л хүчинтэй.
 * Өөр хуудас руу орвол `useClearDraftOnLeave` шууд цэвэрлэдэг (мөн store нь
 * модуль singleton тул Add.js unmount дээр `resetStore`-ыг зэрэг дуудна).
 *
 * Хүргэлт (views/orderDelivery/Add/draft.js), буцаалт (views/orderReturn/Add/draft.js),
 * татан авалт (views/orders/draft.js) дээр ижил механизм байгаа — зан төлөвийг
 * өөрчлөх бол бүгдийг харна.
 *
 * Тэмдэглэл: энэ урсгалд DatePicker/TimePicker байхгүй тул JSON-оос сэргээхэд
 * dayjs объект эргүүлэх шаардлагагүй (бусад урсгалын `reviveFormValues`-ыг үзнэ).
 */
export const EXPRESS_DRAFT_KEY = 'add_express_delivery_draft';

/**
 * Draft-ын key-г merchant тус бүрд салгана.
 *
 * `changeMerchant` (store/createUserSlice.js) нь merchant солиход
 * `window.location.href`-ээр НЭГ ЛЭ таб дотор дахин уншуулдаг тул sessionStorage
 * хэвээрээ үлддэг. Key нь тогтмол байсан бол өмнөх merchant дээр сонгосон
 * салбар, бараа шинэ merchant-ийн формд сэргэх байсан.
 */
export const expressDraftKey = (merchantCode) =>
  merchantCode ? `${EXPRESS_DRAFT_KEY}_${merchantCode}` : EXPRESS_DRAFT_KEY;

/**
 * Draft-д хадгалах store-ын талбарууд.
 *
 * `reference`, `products`, `addresses` нь API-аас дахин уншигддаг (Step1,
 * Order-ийн mount) тул хадгалахгүй — sessionStorage-ыг хэрэггүй өгөгдлөөр
 * дүүргэхгүйн тулд.
 */
const DRAFT_FIELDS = [
  'step1Values',
  'step1FormValues',
  'addressDetails',
  'selectedAddresses',
  'customFieldsList',
  'positions',
  'deliveryTime',
  'deliveryType',
];

/** Store-ын төлвөөс зөвхөн хадгалах талбаруудыг шүүнэ */
export const pickDraft = (state = {}) =>
  DRAFT_FIELDS.reduce((draft, key) => {
    if (state[key] !== undefined) draft[key] = state[key];
    return draft;
  }, {});

export const readDraft = (key) => {
  if (!key) return {};

  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('readDraft error: ', error);
    return {};
  }
};

export const writeDraft = (key, draft) => {
  if (!key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Хэмжээ хэтэрсэн зэрэг тохиолдолд урсгалыг тасалдуулахгүй
    console.error('writeDraft error: ', error);
  }
};

/** Захиалга амжилттай үүссэний дараа дуудаж draft-ыг цэвэрлэнэ */
export const clearDraft = (key) => {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('clearDraft error: ', error);
  }
};

/**
 * Хуудсаас гармагц draft-ыг цэвэрлэнэ.
 *
 * Захиалга үүсгэх хуудас unmount болох (өөр хуудас руу орох, гарчгийн "back",
 * breadcrumb, хажуугийн меню, browser-ийн back — бүгд) үед л cleanup ажиллана.
 * Тиймээс хагас бөглөсөн мэдээлэл дараа хуудсанд дахин орж ирэхэд сэргэхгүй.
 *
 * Refresh дээр ХАМААРАХГҮЙ: хуудас дахин уншихад JS бүхэлдээ унтардаг тул
 * React cleanup ажиллаж чаддаггүй — refresh-ийн сэргээлт хэвээрээ.
 *
 * Алхам (`?step=`), таб (`?tab=`) солих нь зөвхөн search-ийг өөрчилдөг, route
 * нэг хэвээрээ тул unmount болохгүй — draft хадгалагдана.
 */
export const useClearDraftOnLeave = (key) => {
  useEffect(() => () => clearDraft(key), [key]);
};
